import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Clock, Flame, Loader2, XCircle, CheckCircle2, CalendarPlus, Star } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { fmt12 } from "../lib/time";
import { GOOGLE_REVIEW_URL } from "../lib/brand";

const ManageBookingPage = () => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState(null);
  const [newTime, setNewTime] = useState("");
  const [taken, setTaken] = useState([]);
  const [available, setAvailable] = useState([]);
  const [dayClosed, setDayClosed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [hours, setHours] = useState(null);

  const load = () => api.get(`/bookings/manage/${id}`).then(r => { setBooking(r.data); }).catch(() => setBooking(null)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/blocked-dates").then(r => setBlocks(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/business-hours").then(r => setHours(r.data)).catch(() => {}); }, []);

  const isBlocked = (d) => {
    const ds = format(d, "yyyy-MM-dd");
    if (blocks.some(b => b.start_date <= ds && ds <= b.end_date)) return true;
    if (hours) {
      const wd = (d.getDay() + 6) % 7;
      const cfg = hours.days.find(x => x.day === wd);
      if (cfg && !cfg.open) return true;
    }
    return false;
  };

  useEffect(() => {
    if (!newDate) { setTaken([]); setAvailable([]); setDayClosed(false); return; }
    const d = format(newDate, "yyyy-MM-dd");
    const qs = booking?.service_id ? `?service_id=${encodeURIComponent(booking.service_id)}&exclude_booking_id=${encodeURIComponent(id)}` : "";
    api.get(`/bookings/slots/${d}${qs}`).then(r => {
      setTaken(r.data.taken || []);
      setAvailable(r.data.slots || []);
      setDayClosed(!r.data.day_open);
      if (newTime && !(r.data.slots || []).includes(newTime)) setNewTime("");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDate, booking?.service_id]);

  const reschedule = async () => {
    if (!newDate || !newTime) return toast.error("Pick a new date and time");
    setSaving(true);
    try {
      await api.post(`/bookings/manage/${id}/reschedule`, { date: format(newDate, "yyyy-MM-dd"), time: newTime });
      toast.success("Booking rescheduled");
      setNewDate(null); setNewTime("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reschedule failed");
    } finally { setSaving(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await api.post(`/bookings/manage/${id}/cancel`);
      toast.success("Booking cancelled");
      load();
    } catch (e) { toast.error("Cancel failed"); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="text-[#D90429] animate-spin"/></div>;
  if (!booking) return (
    <div className="min-h-[60vh] flex items-center" data-testid="manage-not-found">
      <div className="max-w-md mx-auto px-6 py-16 border border-[#262626] bg-[#0a0a0a] text-center">
        <XCircle size={40} className="text-[#EF4444] mx-auto mb-4"/>
        <h1 className="font-display text-2xl font-bold uppercase">Booking not found</h1>
        <p className="mt-3 text-[#A3A3A3] text-sm">Check the link or contact us for help.</p>
        <Link to="/" className="mt-6 inline-block bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-6 py-3 transition-colors">Back home</Link>
      </div>
    </div>
  );

  const isCancelled = booking.status === "cancelled";

  return (
    <div data-testid="manage-page">
      <section className="pt-24 pb-12 border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">Manage Booking</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
            {isCancelled ? <>Booking <span className="text-[#EF4444]">cancelled.</span></> : <>Your booking, <span className="text-[#D90429]">your call.</span></>}
          </h1>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12 space-y-6">
          <div className="border border-[#262626] bg-[#0a0a0a] p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display uppercase tracking-[0.25em] text-[10px] text-[#D90429]">Status</div>
                <div className="mt-1 font-display text-xl font-bold uppercase">{booking.status}</div>
              </div>
              <CheckCircle2 size={28} className={isCancelled ? "text-[#EF4444]" : "text-[#10B981]"} />
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
              <Row label="Service" value={`${booking.service_name}${booking.vehicle_type ? ` (${booking.vehicle_type})` : ""}`} />
              <Row label="Date" value={booking.date} />
              <Row label="Time" value={fmt12(booking.time)} />
              <Row label="Vehicle" value={booking.vehicle_make_model} />
              <Row label="Customer" value={booking.name} />
              <Row label="Total Paid" value={`$${booking.amount.toFixed(2)}`} />
            </div>
            {!isCancelled && (
              <a
                href={`${API}/bookings/${booking.id}/calendar.ics`}
                data-testid="manage-add-to-calendar-btn"
                className="mt-6 inline-flex items-center gap-2 border border-[#262626] hover:border-[#D90429] text-white font-display font-bold tracking-wider uppercase px-5 py-3 text-sm transition-colors"
              >
                <CalendarPlus size={14}/> Add to calendar
              </a>
            )}
          </div>

          {booking.status === "completed" && (
            <div className="border-2 border-[#D90429] bg-[#D90429]/10 p-6 md:p-8 text-center" data-testid="manage-review-cta">
              <div className="flex justify-center mb-3">
                {[0,1,2,3,4].map(i => <Star key={i} size={20} className="text-[#D90429] fill-[#D90429]"/>)}
              </div>
              <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429]">Service Complete</div>
              <h2 className="mt-2 font-display text-2xl font-black uppercase tracking-tight">Loved it? Leave a quick review</h2>
              <p className="mt-2 text-sm text-[#D4D4D4]">30 seconds. Your honest review helps your neighbors find us.</p>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="manage-review-cta-btn"
                className="mt-4 inline-flex items-center gap-2 bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-7 py-3 text-sm transition-colors"
              >
                <Star size={14} className="fill-white"/> Leave a 5-Star Review
              </a>
            </div>
          )}

          {!isCancelled && (
            <div className="border border-[#262626] bg-[#0a0a0a] p-6">
              <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-4">Reschedule</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">New Date</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button data-testid="manage-date" className="mt-2 w-full h-12 bg-[#050505] border border-[#262626] hover:border-[#D90429] focus:border-[#D90429] px-4 text-left flex items-center justify-between transition-colors">
                        <span className={newDate ? "text-white" : "text-[#A3A3A3]"}>{newDate ? format(newDate, "PPP") : "Pick a date"}</span>
                        <CalendarIcon size={16} className="text-[#A3A3A3]"/>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="bg-[#121212] border-[#262626] rounded-none p-0">
                      <Calendar mode="single" selected={newDate} onSelect={setNewDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || isBlocked(d)} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">New Time</label>
                  <Select value={newTime} onValueChange={setNewTime} disabled={!newDate || dayClosed || available.length === 0}>
                    <SelectTrigger className="mt-2 bg-[#050505] border border-[#262626] focus:border-[#D90429] rounded-none h-12" data-testid="manage-time">
                      <SelectValue placeholder={!newDate ? "Pick a date first" : dayClosed ? "Closed this day" : "Pick a time"}/>
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#262626] rounded-none">
                      {available.map((t) => {
                        const isTaken = taken.includes(t) && !(booking.date === (newDate ? format(newDate, "yyyy-MM-dd") : "") && booking.time === t);
                        return (
                          <SelectItem key={t} value={t} disabled={isTaken} data-testid={`manage-time-${t}`}>
                            {fmt12(t)}{isTaken ? " — booked" : ""}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={reschedule} disabled={saving} data-testid="manage-reschedule-btn" className="bg-[#D90429] hover:bg-[#EF233C] disabled:bg-[#262626] text-white font-display font-bold tracking-wider uppercase px-6 py-3 inline-flex items-center gap-2 transition-colors">
                  <Flame size={14}/> {saving ? "Saving…" : "Confirm new time"}
                </button>
                <button onClick={cancel} data-testid="manage-cancel-btn" className="border border-[#262626] hover:border-[#EF4444] hover:text-[#EF4444] text-white font-display font-bold tracking-wider uppercase px-6 py-3 transition-colors">
                  Cancel booking
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div>
    <div className="font-display uppercase tracking-[0.2em] text-[10px] text-[#A3A3A3]">{label}</div>
    <div className="mt-1 text-white">{value}</div>
  </div>
);

export default ManageBookingPage;
